import apiClient from './apiClient';

// ==========================================
// TypeScript Interfaces - Matching API Contract
// ==========================================

export type PartnershipType = 'hotel' | 'event_planner' | 'promoter';
export type PartnershipStatus = 'active' | 'inactive';
export type CommissionTierName = 'bronze' | 'silver' | 'gold' | 'platinum';
export type CommissionModel = 'flat' | 'tiered';
export type CommissionStatus = 'pending' | 'approved' | 'paid' | 'rejected';
export type ApplicationType = 'individual' | 'corporate';
export type ApplicationStatus = 'pending' | 'approved' | 'rejected';

// Partnership Type Definition
export interface PartnershipTypeDefinition {
  id: string;
  typeCode: PartnershipType;
  displayName: string;
  description: string;
  baseCommissionRate: number;
  commissionModel: CommissionModel;
  features: Record<string, boolean>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Commission Tier
export interface CommissionTier {
  id: string;
  partnershipTypeId: string;
  partnershipTypeCode: PartnershipType;
  partnershipTypeName: string;
  tierName: CommissionTierName;
  tierLevel: number;
  displayName: string;
  minVolume: number;
  maxVolume: number | null;
  commissionRate: number;
  benefits: Record<string, boolean>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Partnership (Activated Corporate Account)
export interface Partnership {
  id: string; // corporate_account_id
  companyName: string;
  email: string;
  phone?: string;
  contactPerson?: string;
  partnershipType: PartnershipType;
  partnershipTypeName: string;
  partnershipTypeId: string;
  referralCode: string;
  isActive: boolean;
  activatedAt: string;
  customCommissionRate?: number | null;
  accountManagerId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// YTD Statistics
export interface YTDStats {
  totalBookings: number;
  totalRevenue: number;
  totalCommissions: number;
  avgBookingValue: number;
  uniqueCustomers: number;
  repeatCustomerRate: number;
  conversionRate: number;
}

// Current Tier Info
export interface CurrentTierInfo {
  tierId: string;
  tierName: CommissionTierName;
  displayName: string;
  tierLevel: number;
  currentCommissionRate: number;
  minVolume: number;
  maxVolume: number | null;
  volumeToNextTier: number | null;
  progressToNextTier: number | null;
  benefits: Record<string, boolean>;
}

// Top Referral
export interface TopReferral {
  customerName: string;
  totalBookings: number;
  totalRevenue: number;
  firstReferredAt: string;
  lastBookingAt: string;
}

// Commission Summary
export interface CommissionSummary {
  pending: { count: number; amount: number };
  approved: { count: number; amount: number };
  paid: { count: number; amount: number };
  rejected: { count: number; amount: number };
}

// Recent Activity
export interface RecentActivity {
  date: string;
  type: 'booking_confirmed' | 'commission_paid' | 'tier_upgrade' | 'commission_approved';
  description: string;
  commissionAmount?: number;
  amount?: number;
  newTier?: string;
}

// Partnership Dashboard
export interface PartnershipDashboard {
  partnership: Partnership;
  ytdStats: YTDStats;
  currentTier: CurrentTierInfo;
  topReferrals: TopReferral[];
  commissionSummary: CommissionSummary;
  recentActivity: RecentActivity[];
}

// Filters
export interface PartnershipFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: PartnershipType | 'all';
  isActive?: boolean | 'all';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'companyName' | 'activatedAt' | 'totalRevenue' | 'totalBookings';
  sortOrder?: 'asc' | 'desc';
}

// List Response
export interface PartnershipListResponse {
  partnerships: Partnership[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  summary: {
    totalActive: number;
    totalInactive: number;
    totalRevenue: number;
    totalCommissions: number;
  };
}

// Activation Request
export interface ActivatePartnershipRequest {
  corporateAccountId: string;
  partnershipType: PartnershipType;
  customCommissionRate?: number;
  accountManagerId?: string;
}

// Partnership Application
export interface PartnershipApplication {
  id: string;
  userId: string;
  applicantName?: string;
  applicantEmail?: string;
  partnershipType: PartnershipType;
  partnershipTypeName: string;
  applicationType: ApplicationType;
  status: ApplicationStatus;
  companyName?: string;
  companyRegistrationNumber?: string;
  billingEmail?: string;
  city?: string;
  state?: string;
  industry?: string;
  expectedMonthlyReferrals?: number;
  notes?: string;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Application Filters
export interface ApplicationFilters {
  page?: number;
  limit?: number;
  status?: ApplicationStatus | 'all';
  partnershipType?: PartnershipType | 'all';
  applicationType?: ApplicationType | 'all';
  search?: string;
}

// Application List Response
export interface ApplicationListResponse {
  applications: PartnershipApplication[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  summary: {
    totalPending: number;
    totalApproved: number;
    totalRejected: number;
  };
}

// Service Response
export interface ServiceResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  details?: string;
}

// ==========================================
// Service Implementation
// ==========================================

export const partnershipsService = {
  /**
   * Get all partnership types
   */
  async getPartnershipTypes(): Promise<ServiceResponse<{ partnershipTypes: PartnershipTypeDefinition[]; total: number }>> {
    try {
      const response = await apiClient.get('/api/v1/admin/partnerships/types');
      const apiData = response.data;
      return {
        success: true,
        data: apiData.data || apiData,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch partnership types',
      };
    }
  },

  /**
   * Get commission tiers
   */
  async getCommissionTiers(partnershipTypeId?: string): Promise<ServiceResponse<{ tiers: CommissionTier[]; total: number }>> {
    try {
      const params = partnershipTypeId ? `?partnershipTypeId=${partnershipTypeId}` : '';
      const response = await apiClient.get(`/api/v1/admin/partnerships/tiers${params}`);
      const apiData = response.data;
      return {
        success: true,
        data: apiData.data || apiData,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch commission tiers',
      };
    }
  },

  /**
   * Get all partnerships with filters
   */
  async getPartnerships(filters?: PartnershipFilters): Promise<ServiceResponse<PartnershipListResponse>> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.search) params.append('search', filters.search);
      if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters?.isActive !== undefined && filters.isActive !== 'all') {
        params.append('isActive', filters.isActive.toString());
      }
      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await apiClient.get(`/api/v1/admin/partnerships?${params.toString()}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch partnerships',
      };
    }
  },

  /**
   * Get partnership dashboard
   */
  async getPartnershipDashboard(partnershipId: string): Promise<ServiceResponse<PartnershipDashboard>> {
    try {
      const response = await apiClient.get(`/api/v1/admin/partnerships/${partnershipId}/dashboard`);

      // Backend returns {success: true, data: {...}}, so extract response.data.data
      const apiData = response.data.data || response.data;

      // Transform backend response to match frontend interface
      const dashboardData: PartnershipDashboard = {
        partnership: {
          ...apiData.partnership,
          partnershipType: apiData.partnershipType?.code || apiData.partnership.partnershipType,
          partnershipTypeName: apiData.partnershipType?.name || apiData.partnership.partnershipTypeName,
          partnershipTypeId: apiData.partnership.partnershipTypeId,
          isActive: apiData.partnership.status === 'active',
        },
        ytdStats: {
          totalBookings: apiData.performance?.totalBookings || 0,
          totalRevenue: apiData.performance?.totalRevenue || 0,
          totalCommissions: apiData.statistics?.ytdCommissionEarned || 0,
          avgBookingValue: apiData.performance?.avgBookingValue || 0,
          uniqueCustomers: apiData.performance?.activeReferrals || 0,
          repeatCustomerRate: 0, // Not provided by backend
          conversionRate: apiData.performance?.conversionRate || 0,
        },
        currentTier: apiData.tierInfo || {
          tierId: '',
          tierName: 'bronze' as CommissionTierName,
          displayName: 'Bronze',
          tierLevel: 1,
          currentCommissionRate: apiData.statistics?.currentCommissionRate || apiData.partnershipType?.baseCommissionRate || 5,
          minVolume: 0,
          maxVolume: null,
          volumeToNextTier: null,
          progressToNextTier: null,
          benefits: {},
        },
        topReferrals: apiData.topReferrals || [],
        commissionSummary: {
          pending: {
            count: apiData.commissions?.pendingCount || 0,
            amount: apiData.commissions?.pendingAmount || 0,
          },
          approved: {
            count: apiData.commissions?.approvedCount || 0,
            amount: apiData.commissions?.approvedAmount || 0,
          },
          paid: {
            count: apiData.commissions?.paidCount || 0,
            amount: apiData.commissions?.paidAmount || 0,
          },
          rejected: {
            count: apiData.commissions?.rejectedCount || 0,
            amount: apiData.commissions?.rejectedAmount || 0,
          },
        },
        recentActivity: (apiData.recentCommissions || []).map((commission: any) => ({
          date: commission.createdAt || commission.date,
          type: 'commission_paid' as const,
          description: commission.description || `Commission for booking #${commission.bookingId}`,
          commissionAmount: commission.amount,
          amount: commission.amount,
        })),
      };

      return {
        success: true,
        data: dashboardData,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch partnership dashboard',
      };
    }
  },

  /**
   * Activate a corporate account as a partnership
   */
  async activatePartnership(request: ActivatePartnershipRequest): Promise<ServiceResponse<Partnership>> {
    try {
      const response = await apiClient.post('/api/v1/admin/partnerships/activate', request);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership activated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to activate partnership',
      };
    }
  },

  /**
   * Suspend a partnership
   */
  async suspendPartnership(partnershipId: string, reason?: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.post(`/api/v1/admin/partnerships/${partnershipId}/suspend`, { reason });

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership suspended successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to suspend partnership',
      };
    }
  },

  /**
   * Delete a partnership permanently
   */
  async deletePartnership(partnershipId: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.post(`/api/v1/admin/partnerships/${partnershipId}/permanently-delete`, {});
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership permanently deleted',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete partnership',
      };
    }
  },

  /**
   * Reactivate a partnership
   */
  async reactivatePartnership(partnershipId: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.post(`/api/v1/admin/partnerships/${partnershipId}/reactivate`);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership reactivated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to reactivate partnership',
      };
    }
  },

  /**
   * Set custom commission rate
   */
  async setCustomCommissionRate(partnershipId: string, customCommissionRate: number): Promise<ServiceResponse> {
    try {
      const response = await apiClient.put(
        `/api/v1/admin/partnerships/${partnershipId}/custom-rate`,
        { customCommissionRate }
      );

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Custom commission rate set successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to set custom commission rate',
      };
    }
  },

  /**
   * Remove custom commission rate
   */
  async removeCustomCommissionRate(partnershipId: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.delete(`/api/v1/admin/partnerships/${partnershipId}/custom-rate`);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Custom commission rate removed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to remove custom commission rate',
      };
    }
  },

  // ==========================================
  // Partnership Applications Management
  // ==========================================

  /**
   * Get all partnership applications with filters
   */
  async getApplications(filters?: ApplicationFilters): Promise<ServiceResponse<ApplicationListResponse>> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters?.partnershipType && filters.partnershipType !== 'all') {
        params.append('partnershipType', filters.partnershipType);
      }
      if (filters?.applicationType && filters.applicationType !== 'all') {
        params.append('applicationType', filters.applicationType);
      }

      const response = await apiClient.get(`/api/v1/admin/partnerships/applications?${params.toString()}`);

      // Transform API response to match frontend expectations
      const apiData = response.data.data;

      if (!apiData || !apiData.applications) {
        return {
          success: false,
          error: 'Invalid response format from API',
        };
      }

      // Map partnership type codes to display names
      const typeNameMap: Record<string, string> = {
        'hotel': 'Hotel Partnership',
        'event_planner': 'Event Planner',
        'promoter': 'Promoter',
      };

      const transformedData: ApplicationListResponse = {
        applications: apiData.applications.map((app: any) => ({
          id: app.id,
          userId: app.userId,
          applicantName: app.applicant ? `${app.applicant.firstName} ${app.applicant.lastName}` : undefined,
          applicantEmail: app.applicant?.email,
          partnershipType: app.partnershipType,
          partnershipTypeName: typeNameMap[app.partnershipType] || app.partnershipType,
          applicationType: app.applicationType,
          status: app.status,
          companyName: app.companyName,
          companyRegistrationNumber: app.companyRegistrationNumber,
          billingEmail: app.contactEmail,
          city: app.location?.city,
          state: app.location?.state,
          industry: app.industry,
          expectedMonthlyReferrals: app.expectedMonthlyReferrals,
          notes: app.notes,
          rejectionReason: app.rejectionReason,
          reviewedBy: app.reviewedBy,
          reviewedAt: app.reviewedAt,
          createdAt: app.createdAt,
          updatedAt: app.updatedAt,
        })),
        pagination: {
          total: apiData.total || 0,
          page: filters?.page || 1,
          limit: filters?.limit || 100,
          totalPages: Math.ceil((apiData.total || 0) / (filters?.limit || 100)),
        },
        summary: {
          totalPending: apiData.summary?.pending || 0,
          totalApproved: apiData.summary?.approved || 0,
          totalRejected: apiData.summary?.rejected || 0,
        },
      };

      return {
        success: true,
        data: transformedData,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch partnership applications',
      };
    }
  },

  /**
   * Approve partnership application
   */
  async approveApplication(applicationId: string): Promise<ServiceResponse<PartnershipApplication>> {
    try {
      const response = await apiClient.post(
        `/api/v1/admin/partnerships/applications/${applicationId}/approve`
      );

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership application approved successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to approve application',
      };
    }
  },

  /**
   * Reject partnership application
   */
  async rejectApplication(
    applicationId: string,
    rejectionReason?: string
  ): Promise<ServiceResponse<PartnershipApplication>> {
    try {
      const response = await apiClient.post(
        `/api/v1/admin/partnerships/applications/${applicationId}/reject`,
        { rejectionReason }
      );

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership application rejected',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to reject application',
      };
    }
  },

  // ==========================================
  // Partnership Types & Tiers Management
  // ==========================================

  /**
   * Update partnership type (e.g., change base commission rate)
   */
  async updatePartnershipType(
    typeId: string,
    data: {
      baseCommissionRate?: number;
      displayName?: string;
      description?: string;
    }
  ): Promise<ServiceResponse<PartnershipTypeDefinition>> {
    try {
      const response = await apiClient.put(`/api/v1/admin/partnerships/types/${typeId}`, data);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership type updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update partnership type',
      };
    }
  },

  /**
   * Create new commission tier
   */
  async createCommissionTier(data: {
    partnershipTypeId: string;
    tierName: string;
    tierLevel: number;
    displayName: string;
    minVolume: number;
    maxVolume: number | null;
    commissionRate: number;
    benefits?: Record<string, any>;
  }): Promise<ServiceResponse<CommissionTier>> {
    try {
      const response = await apiClient.post('/api/v1/admin/partnerships/tiers', data);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Commission tier created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create commission tier',
        details: error.response?.data?.details,
      };
    }
  },

  /**
   * Update existing commission tier
   */
  async updateCommissionTier(
    tierId: string,
    data: {
      commissionRate?: number;
      displayName?: string;
      minVolume?: number;
      maxVolume?: number | null;
      benefits?: Record<string, any>;
    }
  ): Promise<ServiceResponse<CommissionTier>> {
    try {
      const response = await apiClient.put(`/api/v1/admin/partnerships/tiers/${tierId}`, data);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Commission tier updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update commission tier',
      };
    }
  },

  /**
   * Delete/deactivate commission tier
   */
  async deleteCommissionTier(tierId: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.delete(`/api/v1/admin/partnerships/tiers/${tierId}`);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Commission tier deactivated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to deactivate commission tier',
      };
    }
  },

  /**
   * Create new partnership type
   */
  async createPartnershipType(data: {
    typeCode: string;
    displayName: string;
    description: string;
    baseCommissionRate: number;
    commissionModel: CommissionModel;
    features?: Record<string, boolean>;
  }): Promise<ServiceResponse<PartnershipTypeDefinition>> {
    try {
      const response = await apiClient.post('/api/v1/admin/partnerships/types', data);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership type created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create partnership type',
      };
    }
  },

  /**
   * Delete partnership type
   */
  async deletePartnershipType(typeId: string): Promise<ServiceResponse> {
    try {
      const response = await apiClient.delete(`/api/v1/admin/partnerships/types/${typeId}`);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership type deactivated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to deactivate partnership type',
      };
    }
  },

  // ==========================================
  // Partnership CRUD
  // ==========================================

  /**
   * Get single partnership details
   */
  async getPartnership(partnershipId: string): Promise<ServiceResponse<Partnership>> {
    try {
      const response = await apiClient.get(`/api/v1/admin/partnerships/${partnershipId}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch partnership',
      };
    }
  },

  /**
   * Update partnership details
   */
  async updatePartnership(
    partnershipId: string,
    data: {
      companyName?: string;
      email?: string;
      phone?: string;
      contactPerson?: string;
      accountManagerId?: string;
      notes?: string;
    }
  ): Promise<ServiceResponse<Partnership>> {
    try {
      const response = await apiClient.put(`/api/v1/admin/partnerships/${partnershipId}`, data);

      // Extract the actual data from axios response
      const apiData = response.data;

      return {
        success: true,
        data: apiData.data || apiData,
        message: apiData.message || 'Partnership updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update partnership',
      };
    }
  },


  /**
   * Reset YTD/MTD volumes for partnerships
   */
  async resetPartnershipVolumes(data: {
    resetType: 'yearly' | 'monthly';
    partnershipId?: string;
    confirm: boolean;
  }): Promise<ServiceResponse> {
    try {
      const response = await apiClient.post('/api/v1/admin/partnerships/reset-ytd', data);

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Volumes reset successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to reset volumes',
      };
    }
  },
};
